import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import QRCode from "qrcode";
import { api, setExperienceToken, type Inspection } from "./api";

const PARENTS = ["https://ticketbnk.co.kr", "https://www.ticketbnk.co.kr"];
const DEMO_CODE = /^(MV|NX)[0-9A-F]{12}$/;

type Kind = "valid" | "again" | "cancel" | "period" | "unknown";
type View = "home" | "scan" | "result";
type ScanState = "ready" | "reading" | "done" | "error";

const SCENARIOS: Array<{ kind: Kind; label: string }> = [
  { kind: "valid", label: "정상 입장" },
  { kind: "again", label: "같은 티켓 다시 스캔" },
  { kind: "cancel", label: "취소 티켓" },
  { kind: "period", label: "이용 기간·시간 오류" },
  { kind: "unknown", label: "미등록 티켓" },
];

const COPY: Record<string, { title: string; reason: string }> = {
  ADMITTED: { title: "정상 입장", reason: "이용 시간이 맞고 아직 사용되지 않은 티켓입니다." },
  ALREADY_USED: { title: "이미 사용된 티켓", reason: "같은 티켓이 이미 입장 처리되어 다시 입장할 수 없습니다." },
  CANCELLED: { title: "취소된 티켓", reason: "취소된 예약은 입장할 수 없습니다." },
  INVALID_PERIOD: { title: "이용 기간/시간 오류", reason: "지금 시각이 샘플 이용 기간 밖입니다." },
  UNKNOWN_TICKET: { title: "미등록 티켓", reason: "등록되지 않은 티켓입니다." },
};

function parentOrigin() {
  try {
    const origin = document.referrer ? new URL(document.referrer).origin : "";
    return PARENTS.includes(origin) ? origin : "";
  } catch {
    return "";
  }
}

function notify(event: "ready" | "expired" | "reset-done" | "close") {
  const origin = parentOrigin();
  if (!origin || window.parent === window) return;
  window.parent.postMessage({ source: "mv-experience", event }, origin);
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function decodeDataUrl(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      new BrowserMultiFormatReader()
        .decodeFromImageElement(image)
        .then((result) => resolve(result.getText().trim()))
        .catch(reject);
    };
    image.onerror = () => reject(new Error("qr"));
    image.src = dataUrl;
  });
}

export function ExperienceApp() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error" | "expired">("loading");
  const [notice, setNotice] = useState("체험 화면을 준비하고 있습니다.");
  const [entranceId, setEntranceId] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<View>("home");
  const [scanState, setScanState] = useState<ScanState>("ready");
  const [scanError, setScanError] = useState("");
  const [qrImage, setQrImage] = useState("");
  const [result, setResult] = useState<Inspection | null>(null);
  const [sampleFact, setSampleFact] = useState("");
  const [history, setHistory] = useState<Inspection[]>([]);
  const [hasValid, setHasValid] = useState(false);
  const [activeKind, setActiveKind] = useState<Kind>("valid");
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraNote, setCameraNote] = useState("");
  const [shareImage, setShareImage] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);
  const seenRef = useRef("");
  const generation = useRef(0);
  const busyRef = useRef(false);
  const entranceRef = useRef("");

  function stopCamera() {
    controlsRef.current?.stop();
    controlsRef.current = null;
    const video = videoRef.current;
    if (video?.srcObject) (video.srcObject as MediaStream).getTracks().forEach((track) => track.stop());
    if (video) video.srcObject = null;
    setCameraOn(false);
  }

  async function boot() {
    setPhase("loading");
    setNotice("체험 화면을 준비하고 있습니다.");
    setExperienceToken("");
    try {
      const started = await api.startExperience();
      setExperienceToken(started.token);
      const context = await api.context();
      const entrance = context.entrances.find((item) => item.code === "a-main" && item.vendor_code === "sample-a");
      if (!entrance) throw new Error("샘플 입구를 찾지 못했습니다.");
      entranceRef.current = entrance.id;
      setEntranceId(entrance.id);
      const list = await api.history();
      setHistory(list.items);
      setPhase("ready");
      notify("ready");
    } catch {
      setExperienceToken("");
      setPhase("error");
      setNotice("체험을 시작하지 못했습니다. 다시 시도하거나 새 창으로 열어 보십시오.");
    }
  }

  useEffect(() => {
    boot();
    const onMessage = (event: MessageEvent) => {
      const origin = parentOrigin();
      if (!origin || event.origin !== origin || event.source !== window.parent) return;
      const data = event.data as { source?: string; event?: string } | null;
      if (!data || data.source !== "ticketbnk-experience" || data.event !== "reset") return;
      resetTrial();
    };
    window.addEventListener("message", onMessage);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") notify("close");
    };
    window.addEventListener("keydown", onKey);
    return () => {
      generation.current += 1;
      window.removeEventListener("message", onMessage);
      window.removeEventListener("keydown", onKey);
      controlsRef.current?.stop();
      const video = videoRef.current;
      if (video?.srcObject) (video.srcObject as MediaStream).getTracks().forEach((track) => track.stop());
      setExperienceToken("");
    };
  }, []);

  async function refreshHistory() {
    const list = await api.history();
    setHistory(list.items);
  }

  async function inspectCode(code: string, fact: string, token: number) {
    const row = await api.inspect({
      requestId: crypto.randomUUID(),
      entranceId: entranceRef.current,
      code,
      admitCount: 1,
      admitMode: "partial",
    });
    if (generation.current !== token) return null;
    setResult(row);
    setSampleFact(fact);
    await refreshHistory();
    if (generation.current !== token) return null;
    setView("result");
    return row;
  }

  async function startScan(kind: Kind) {
    if (busyRef.current || !entranceRef.current) return;
    if (kind === "again" && !hasValid) return;
    busyRef.current = true;
    setBusy(true);
    stopCamera();
    const token = ++generation.current;
    setActiveKind(kind);
    setView("scan");
    setScanState("ready");
    setScanError("");
    setQrImage("");
    setResult(null);
    try {
      const issued = await api.issueSample(kind);
      if (generation.current !== token) return;
      if (kind === "valid") setHasValid(true);
      const image = await QRCode.toDataURL(issued.code, { margin: 2, width: 240, errorCorrectionLevel: "M" });
      if (generation.current !== token) return;
      setQrImage(image);
      setSampleFact(issued.fact);
      setScanState("reading");
      const started = Date.now();
      const decoded = await decodeDataUrl(image);
      if (generation.current !== token) return;
      if (decoded !== issued.code) throw new Error("mismatch");
      const remain = 1400 - (Date.now() - started);
      if (remain > 0) await wait(remain);
      if (generation.current !== token) return;
      setScanState("done");
      await wait(400);
      if (generation.current !== token) return;
      const row = await inspectCode(decoded, issued.fact, token);
      if (!row) return;
    } catch (error) {
      if (generation.current !== token) return;
      const message = error instanceof Error ? error.message : "";
      if (message.includes("로그인")) {
        setPhase("expired");
        setNotice("체험 시간이 끝났습니다. 다시 시작하면 새 샘플로 이어집니다.");
        notify("expired");
        setExperienceToken("");
      } else if (message.includes("먼저 정상")) {
        setScanError("먼저 정상 입장 체험을 한 번 진행하십시오.");
        setScanState("error");
      } else {
        setScanError("검표를 처리하지 못했습니다. 다시 시도하십시오.");
        setScanState("error");
      }
    } finally {
      if (generation.current === token) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  }

  function nextScan() {
    generation.current += 1;
    setView("home");
    setScanState("ready");
    setQrImage("");
    setResult(null);
    setScanError("");
  }

  async function resetTrial() {
    generation.current += 1;
    busyRef.current = false;
    stopCamera();
    setBusy(true);
    try {
      await api.resetExperience();
      setResult(null);
      setHasValid(false);
      setSampleFact("");
      setQrImage("");
      setShareImage("");
      setView("home");
      setScanState("ready");
      setScanError("");
      await refreshHistory();
      setPhase("ready");
      setNotice("");
      notify("reset-done");
    } catch {
      setNotice("체험 초기화에 실패했습니다. 다시 시도하십시오.");
      setPhase("error");
    } finally {
      setBusy(false);
    }
  }

  function openCamera() {
    if (cameraOn || busyRef.current) return;
    setCameraNote("");
    setView("home");
    setCameraOn(true);
  }

  useEffect(() => {
    if (!cameraOn || !videoRef.current) return;
    const video = videoRef.current;
    let stopped = false;
    const reader = new BrowserMultiFormatReader();
    reader.decodeFromVideoDevice(undefined, video, (scan) => {
      const text = scan?.getText()?.trim() ?? "";
      if (!text || text === seenRef.current || busyRef.current) return;
      seenRef.current = text;
      if (!DEMO_CODE.test(text)) {
        setCameraNote("데모 샘플 코드만 검표할 수 있습니다. QR 스캔 체험으로 계속할 수 있습니다.");
        return;
      }
      stopped = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
      if (video.srcObject) (video.srcObject as MediaStream).getTracks().forEach((track) => track.stop());
      video.srcObject = null;
      setCameraOn(false);
      const token = ++generation.current;
      busyRef.current = true;
      setBusy(true);
      inspectCode(text, "실제 카메라로 읽은 데모 샘플입니다.", token)
        .catch((error: Error) => {
          if (generation.current !== token) return;
          if (error.message.includes("로그인")) {
            setPhase("expired");
            setNotice("체험 시간이 끝났습니다. 다시 시작하면 새 샘플로 이어집니다.");
            notify("expired");
            setExperienceToken("");
            return;
          }
          setCameraNote("검표를 처리하지 못했습니다. QR 스캔 체험으로 계속할 수 있습니다.");
        })
        .finally(() => {
          if (generation.current === token) {
            busyRef.current = false;
            setBusy(false);
          }
        });
    }).then((controls) => {
      if (stopped) controls.stop();
      else controlsRef.current = controls;
    }).catch((error: Error) => {
      if (stopped) return;
      controlsRef.current = null;
      if (video.srcObject) (video.srcObject as MediaStream).getTracks().forEach((track) => track.stop());
      video.srcObject = null;
      setCameraOn(false);
      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        setCameraNote("카메라 권한이 거부되었습니다. QR 스캔 체험으로 계속할 수 있습니다.");
      } else if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
        setCameraNote("카메라를 찾지 못했습니다. QR 스캔 체험으로 계속할 수 있습니다.");
      } else {
        setCameraNote("카메라를 열 수 없습니다. QR 스캔 체험으로 계속할 수 있습니다.");
      }
    });
    return () => {
      stopped = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
      if (video.srcObject) (video.srcObject as MediaStream).getTracks().forEach((track) => track.stop());
      video.srcObject = null;
    };
  }, [cameraOn]);

  async function showShare() {
    if (!hasValid) return;
    const issued = await api.issueSample("again");
    const image = await QRCode.toDataURL(issued.code, { margin: 2, width: 220, errorCorrectionLevel: "M" });
    setShareImage(image);
  }

  const embedded = window.parent !== window;
  const counts = history.reduce(
    (bag, item) => {
      bag.total += 1;
      if (item.decision === "ADMITTED") bag.pass += 1;
      else bag.block += 1;
      return bag;
    },
    { total: 0, pass: 0, block: 0 },
  );
  const scanLabel = scanState === "done" ? "인식 완료" : scanState === "reading" ? "인식 중" : scanState === "error" ? "다시 시도" : "스캔 준비";

  return (
    <div className="shell shell--experience">
      <header className="validator-app__header">
        {view === "home" ? (
          <div className="validator-app__header-title">모바일 검표 체험</div>
        ) : (
          <button className="validator-app__back" type="button" aria-label="돌아가기" onClick={nextScan}><i /></button>
        )}
        {!embedded && view === "home" && <span className="va-exp__badge">샘플 환경</span>}
        {view !== "home" && <div className="validator-app__header-title">QR 스캔 체험</div>}
      </header>
      <main className="shell__main va-exp">
        {phase !== "ready" && (
          <section className="va-panel">
            <p>{notice}</p>
            {phase !== "loading" && (
              <button className="va-btn" type="button" onClick={boot}>다시 시작</button>
            )}
          </section>
        )}
        {phase === "ready" && view === "scan" && (
          <section className="va-panel va-scanplay">
            <h2>QR코드를 스캔해주세요</h2>
            <p className="va-note">샘플 QR을 이용한 스캔 체험입니다.</p>
            <div className="va-scanplay__frame" aria-hidden={scanState === "error"}>
              <span className="va-scanplay__corner is-tl" />
              <span className="va-scanplay__corner is-tr" />
              <span className="va-scanplay__corner is-bl" />
              <span className="va-scanplay__corner is-br" />
              {qrImage && <img alt="" src={qrImage} />}
              <span className="va-scanplay__line" />
            </div>
            <p className="va-scanplay__status" role="status">{scanLabel}</p>
            {scanError && <p>{scanError}</p>}
            {scanState === "error" && (
              <button className="va-btn" type="button" onClick={() => startScan(activeKind)}>다시 시도</button>
            )}
            <button className="va-btn va-btn--ghost" type="button" onClick={nextScan}>취소</button>
          </section>
        )}
        {phase === "ready" && view === "result" && result && (
          <>
            <section className={`va-result__panel is-${result.decision === "ADMITTED" ? "pass" : result.decision === "INVALID_PERIOD" ? "warn" : "fail"}`}>
              <h2 className="va-result__title">{COPY[result.decision]?.title ?? "검표 결과"}</h2>
              <p className="va-result__desc">{sampleFact}</p>
              <p className="va-result__desc">{result.detail.message || COPY[result.decision]?.reason || "판정 결과를 확인하십시오."}</p>
              <p className="va-result__desc">표시 코드 {result.codeMask ?? ""} · 발권 참조 {result.vendorTicketRef ?? "-"}</p>
            </section>
            <button className="va-btn" type="button" onClick={nextScan}>다음 검표</button>
          </>
        )}
        {phase === "ready" && view === "home" && (
          <>
            {!embedded && <p className="va-note">샘플 티켓으로 검표합니다. 실제 티켓과 출입 장비에는 영향을 주지 않습니다.</p>}
            <button className="va-btn" type="button" disabled={busy || !entranceId} onClick={() => startScan("valid")}>QR 스캔 체험</button>
            <div className="va-exp__samples">
              {SCENARIOS.filter((item) => item.kind !== "valid").map((item) => (
                <button
                  key={item.kind}
                  className="va-btn va-btn--ghost"
                  type="button"
                  disabled={busy || (item.kind === "again" && !hasValid)}
                  onClick={() => startScan(item.kind)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </>
        )}
        {phase === "ready" && view !== "scan" && (
          <>
            <section className="va-scan__grid" aria-label="이번 체험 통계">
              <div className="va-scan__card"><span>검표</span><strong>{counts.total}</strong></div>
              <div className="va-scan__card is-green"><span>정상</span><strong>{counts.pass}</strong></div>
              <div className="va-scan__card is-red"><span>차단</span><strong>{counts.block}</strong></div>
            </section>
            <section className="va-panel va-exp__history">
              <h2>이번 체험 이력</h2>
              {history.length === 0 && <p>아직 검표 이력이 없습니다.</p>}
              {history.slice(0, 8).map((item) => (
                <p key={item.id}>{COPY[item.decision]?.title ?? item.decision} · {item.codeMask}</p>
              ))}
            </section>
            {view === "home" && (
              <section className="va-panel">
                <h2>실제 카메라로 스캔</h2>
                <p>이 버튼을 누를 때만 카메라 권한을 요청합니다. 데모 샘플 코드만 검표합니다.</p>
                {!cameraOn && <button className="va-btn va-btn--ghost" type="button" onClick={openCamera}>실제 카메라로 스캔</button>}
                {cameraNote && <p>{cameraNote}</p>}
                {cameraNote && <button className="va-btn" type="button" onClick={() => startScan("valid")}>QR 스캔 체험으로 계속하기</button>}
                {cameraOn && (
                  <div className="va-scan__box is-live">
                    <video ref={videoRef} muted playsInline />
                  </div>
                )}
                {cameraOn && <button className="va-btn va-btn--ghost" type="button" onClick={stopCamera}>카메라 닫기</button>}
              </section>
            )}
            {view === "home" && (
              <section className="va-panel">
                <h2>다른 기기에서 스캔</h2>
                <p>기본 체험에는 필요 없습니다. 다른 기기에 표시한 뒤, 그 기기가 아닌 쪽에서 실제 카메라로 읽으십시오.</p>
                <button className="va-btn va-btn--ghost" type="button" disabled={!hasValid} onClick={showShare}>샘플 QR 보기</button>
                {!hasValid && <p>정상 입장 체험 후 같은 샘플을 표시할 수 있습니다.</p>}
                {shareImage && <img alt="" src={shareImage} />}
                {shareImage && <a href={shareImage} download="sample-qr.png">다운로드</a>}
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
